import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, DropdownButton, FlexBox, Icon, List, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useCopyToClipboard } from 'react-use';
import { getChannelName } from 'app/utils/channelName';
import ConversationSummaryHelper from 'app/utils/conversationSummaryHelper';
import { analytics } from 'app/utils/segment';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { CHANNEL_ICON } from 'components/conversationsOverview/components/ConversationDetails/channelIcon';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';
import styles from './conversationHeadline.module.scss';
const copyToClipboardTooltip = 'Click to copy ID';
const copiedToClipboardTooltip = 'ID copied';
const failedToCopyClipboardTooltip = 'Failed to copy ID';
export const ConversationHeadline = () => {
    const [tooltipLabel, setTooltipLabel] = useState(copyToClipboardTooltip);
    const [metaConversation, requester, conversation] = useAppSelector((state) => [
        state.conversation.metaConversation,
        state.conversation.metaConversation.requester,
        state.conversation.metaConversation.conversation,
    ], shallowEqual);
    const [{ error: copyToClipboardError }, copyToClipboard] = useCopyToClipboard();
    const handleCopyToClipboardClick = useCallback(() => {
        var _a;
        copyToClipboard(((_a = metaConversation.conversation.id) === null || _a === void 0 ? void 0 : _a.toString()) || '');
        if (copyToClipboardError) {
            setTooltipLabel(failedToCopyClipboardTooltip);
        }
        else {
            setTooltipLabel(copiedToClipboardTooltip);
        }
        const tooltipTimeout = setTimeout(() => {
            setTooltipLabel(copyToClipboardTooltip);
            clearTimeout(tooltipTimeout);
        }, 2000);
    }, [
        copyToClipboard,
        setTooltipLabel,
        copyToClipboardError,
        metaConversation,
    ]);
    const conversationSummaryHelper = new ConversationSummaryHelper(metaConversation);
    const handleDropdownToggle = useCallback((isOpen) => isOpen && analytics.conversationHeaderConversationDetailsMenuOpened(), []);
    const userName = requester && getUserDisplayName(requester);
    const title = conversation.is_abandoned ? `Abandoned: ${userName}` : userName;
    const info = conversationSummaryHelper.getInfoListLines();
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    useCommandHandler({
        name: 'conversation:details',
        actionFn: () => {
            setIsDetailsOpen((prev) => !prev);
        },
    });
    const detailsTooltipContent = (_jsx(CommandKeybindingsTooltipContent, Object.assign({ commandName: "conversation:details" }, { children: "Conversation details" })));
    return (_jsxs(_Fragment, { children: [_jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200" }, { children: [conversation.initial_channel && (_jsx(Tooltip, Object.assign({ wrap: "div", wrapProps: { className: styles.iconTooltipWrapper }, dark: true, content: getChannelName(conversation.initial_channel) }, { children: _jsx(Icon, { name: CHANNEL_ICON[conversation.initial_channel] }) }))), requester && _jsx(Text.Heading, Object.assign({ variant: "heading-1" }, { children: title })), _jsxs(FlexBox, Object.assign({ alignItems: "center" }, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: tooltipLabel, wrapProps: {
                                    // A small hack to stop the tooltip hiding on click
                                    onMouseDown: (e) => e.preventDefault(),
                                    onClick: (e) => e.preventDefault(),
                                } }, { children: _jsxs(Button, Object.assign({ variant: "tertiary", className: styles.csid, onClick: handleCopyToClipboardClick, styleText: false }, { children: ["#", metaConversation.conversation.id] })) })), _jsx(Tooltip, Object.assign({ content: detailsTooltipContent, dark: true }, { children: _jsx(DropdownButton, { show: isDetailsOpen, icon: "angle-down", buttonProps: {
                                        variant: 'tertiary',
                                    }, onToggle: handleDropdownToggle, fixed: true, dropdownProps: {
                                        className: styles.dropdown,
                                    }, dropdown: _jsx(List, { children: info.map(({ key, value }) => (_jsx(List.Item, { className: styles.infoItem, withHoverEffect: false, contentLeft: _jsx(Text.Paragraph, Object.assign({ className: styles.infoItemLeft }, { children: key })), contentRight: _jsx(Text.Paragraph, Object.assign({ className: styles.infoItemRight }, { children: value })) }, key))) }) }) }))] }))] })), _jsx(Text.Paragraph, { children: conversationSummaryHelper.getSubject() })] }));
};
